import React from "react"
import { Form, Input, Select } from "antd"
import { notify } from "shared/ui/Notifications/notify"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { useEngine } from "hooks/api/useEngine"
import { Project } from "stores/projectStore"
import { SelectOption } from "types/ui.types"
import { Variant } from "types/questionnaire.types"
import { EXPORT_VARIABLE_TYPES } from "types/engineExport.types"

/**
 * Form values interface for the Confirmit export form.
 */
interface ConfirmitFormValues {
  variant: string
  languages: string // Still single string (single-select), but field name matches API
  filename: string
  "variable-names"?: EXPORT_VARIABLE_TYPES
}

/**
 * Props interface for the ExportToConfirmitOptionsForm component.
 */
interface ExportToConfirmitOptionsFormProps {
  project: Project
  setExportConfirmitModalVisible: (visible: boolean) => void
  variants: Variant[]
}

/**
 * ExportToConfirmitOptionsForm component renders a form for configuring options
 * to export a project to Confirmit (Forsta Plus) XML format.
 *
 * The export covers the question structure; routing logic is included in the XML
 * as visible placeholder notes for the Confirmit programmer.
 *
 * @param props - The component props.
 * @returns The rendered ExportToConfirmitOptionsForm component.
 */
const ExportToConfirmitOptionsForm: React.FC<
  ExportToConfirmitOptionsFormProps
> = (props) => {
  const { project, setExportConfirmitModalVisible, variants } = props
  const [form] = Form.useForm<ConfirmitFormValues>()
  const { convertToXMLConfirmit, isConvertingToXMLConfirmit } = useEngine()

  /**
   * Handles the form submission and triggers the export to Confirmit XML.
   *
   * @async
   * @function submitConfirmitOptions
   * @param formValues - The values from the form fields.
   * @returns A promise that resolves when the export is complete.
   */
  const submitConfirmitOptions = async (
    formValues: ConfirmitFormValues
  ): Promise<void> => {
    const { variant, languages, filename } = formValues
    const variableNames =
      formValues["variable-names"] || EXPORT_VARIABLE_TYPES.NUMBERING

    try {
      await convertToXMLConfirmit(project.id, {
        variant,
        languages,
        filename,
        "variable-names": variableNames,
      })

      notify.toast.success("Exported to Confirmit XML successfully!")
      hideModal()
    } catch (error) {
      notify.toast.error("Failed to export to Confirmit XML.")
      console.error("Error exporting to Confirmit XML:", error)
      hideModal()
    }
  }

  /**
   * Resets the form fields and hides the modal.
   *
   * @function hideModal
   */
  const hideModal = (): void => {
    form.resetFields()
    setExportConfirmitModalVisible(false)
  }

  /**
   * Extracts unique languages from an array of variants and a project object.
   * @param variants - An array of variant objects.
   * @param project - A project object containing languages.
   * @returns An array of unique languages.
   */
  const getUniqueLanguages = (
    variants: Variant[],
    project: Project
  ): string[] => {
    const variantLanguages = variants.flatMap((variant) => variant.languages)
    const allLanguages = [...variantLanguages, ...project.Languages]
    return [...new Set(allLanguages)]
  }

  // Default to MASTER if variants array is empty, or if only one language, default it
  const variantOptions: SelectOption[] =
    variants.length > 0
      ? variants.map((variant) => ({
          label: variant.name,
          value: variant.name,
        }))
      : [{ label: "MASTER", value: "MASTER" }]

  const defaultVariant: string | undefined =
    variantOptions.length === 1 ? variantOptions[0].value : undefined

  const languageOptions: SelectOption[] = getUniqueLanguages(
    variants,
    project
  ).map((language) => ({
    label: language,
    value: language,
  }))

  const defaultLanguages: string | undefined =
    languageOptions.length === 1 ? languageOptions[0].value : undefined

  return (
    <Form
      form={form}
      name="export_to_confirmit_options"
      onFinish={submitConfirmitOptions}
      style={{ textAlign: "left" }}
      layout="vertical"
      initialValues={{
        variant: defaultVariant,
        languages: defaultLanguages,
        "variable-names": EXPORT_VARIABLE_TYPES.NUMBERING,
      }}
    >
      <Form.Item
        style={{ marginBottom: "1em" }}
        label="Please enter a filename"
        name="filename"
        validateTrigger="onSubmit"
        rules={[
          {
            required: true,
            message: "Please enter a filename",
          },
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        style={{ marginBottom: "1em" }}
        label="Variant"
        name="variant"
        validateTrigger="onSubmit"
        rules={[
          {
            required: true,
            message: "Please select a variant",
          },
        ]}
      >
        <Select placeholder="Select Variant" options={variantOptions} />
      </Form.Item>
      <Form.Item
        style={{ marginBottom: "1em" }}
        label="Languages"
        name="languages"
        validateTrigger="onSubmit"
        rules={[
          {
            required: true,
            message: "Please select a language",
          },
        ]}
      >
        <Select placeholder="Select Language" options={languageOptions} />
      </Form.Item>
      <Form.Item
        style={{ marginBottom: "1em" }}
        label="Use as variable names"
        name="variable-names"
        validateTrigger="onSubmit"
      >
        <Select
          defaultValue={EXPORT_VARIABLE_TYPES.NUMBERING}
          options={[
            {
              value: EXPORT_VARIABLE_TYPES.NUMBERING,
              label: "Numbered automatically",
            },
            {
              value: EXPORT_VARIABLE_TYPES.LABELS,
              label: "Generate from questionnaire labels",
            },
          ]}
        />
      </Form.Item>

      <div className="flex flex-row mt-5">
        <div className="ml-auto mr-2 space-x-2">
          <DataQButton onClick={hideModal} type="secondary">
            Cancel
          </DataQButton>
          <DataQButton
            type="primary"
            htmlType="submit"
            isLoading={isConvertingToXMLConfirmit}
          >
            Export
          </DataQButton>
        </div>
      </div>
    </Form>
  )
}

export default ExportToConfirmitOptionsForm
